/**
 * @file request-outcome.tsx
 * @module app/modules/service-request/components/request-outcome
 * @description Shows who decided a service request and what happened, under its status. Used by
 * the requester's table and the approver's cards.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import Method from '../../../../utils/methods';
import {
  ServiceRequest,
  ServiceRequestStatus,
} from '../types/service-request.types';

const Status = ServiceRequestStatus;

export const formatRequestDate = (value?: string) =>
  value ? Method.convertDateToDDMMYYYYHHMMAMPM(value) : '';

/** "Decided by Name · date", leaving out whichever part is missing. */
const byLine = (name?: string, at?: string) =>
  [name ? `Decided by ${name}` : '', formatRequestDate(at)]
    .filter(Boolean)
    .join(' · ');

/** Labelled reason box: red for declined and failed, grey for withdrawn. */
const Note = ({
  label,
  text,
  neutral,
}: {
  label: string;
  text?: string;
  neutral?: boolean;
}) =>
  text ? (
    <div className={`sr-reason${neutral ? ' sr-reason--neutral' : ''}`}>
      <span className="sr-reason__label">{label}</span>
      {text}
    </div>
  ) : null;

const RequestOutcome = ({ request }: { request: ServiceRequest }) => {
  const { status, decidedBy, decidedAt, result } = request;
  if (status === Status.Pending) return null;
  if (status === Status.Withdrawn) {
    return (
      <div className="sr-outcome">
        <div className="sr-muted">{formatRequestDate(request.withdrawnAt)}</div>
        <Note label="Reason" text={request.withdrawReason} neutral />
      </div>
    );
  }
  return (
    <div className="sr-outcome">
      <div className="sr-muted">{byLine(decidedBy?.name, decidedAt)}</div>
      {status === Status.Processing ? (
        <div className="sr-muted">Running the action...</div>
      ) : null}
      {status === Status.Completed && result?.message ? (
        <div className="sr-result">{result.message}</div>
      ) : null}
      {status === Status.Failed ? (
        <Note label="Failed" text={result?.message} />
      ) : null}
      {status === Status.Declined ? (
        <Note label="Decline reason" text={request.decisionComment} />
      ) : null}
    </div>
  );
};

export default RequestOutcome;
